import { useState } from "react";
import { Combobox, ETypeOfNotification, InputColumnTableProps, InputSwitch, InputTable, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import { Button } from "primereact/button";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import { useDispatch } from "react-redux";
import { defaultOffice, defaultSector, LoacationType, Office, Sector } from "../../../../../interface/interface";
import { activateOffice, getLocationTypes, inactivateOffice, saveOffice } from "../../../../../servicesHooks/Location/useLocationConfigService";
import useResizeListener from "../../../../../hooks/useResizeListener";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationInlineEditActions from "../../../components/LocationInlineEditActions";

interface OfficeTableProps {
    offices: Office[];
    onChange: (updatedOffices: Office[]) => void;
    onDelete: (office: Office, _idx: number) => void;
    sectors: Sector[];
    mobileTitle?: string;
    mobileSubtitle?: string;
}

export function OfficeTable({ offices, onChange, sectors, onDelete, mobileTitle, mobileSubtitle }: OfficeTableProps) {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);

    const [officeItem, setOfficeItem] = useState<Office>(defaultOffice);
    const [officeErrors, setOfficeErrors] = useState<{ name?: string; officeType?: string; sector?: string }>({});
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [mobileModalVisible, setMobileModalVisible] = useState(false);

    const [selectedSector, setSelectedSector] = useState<Sector | null>(defaultSector)
    const [selectedLocationType, setSelectedLocationType] = useState<LoacationType | null>(null)
    const isEditing = editIndex !== null;

    const resetEditor = () => {
        setOfficeErrors({});
        setSelectedSector(defaultSector);
        setSelectedLocationType(null);
        setOfficeItem(defaultOffice);
        setEditIndex(null);
    };

    const handleAdd = () => {
        void executeAdd();
    };

    const executeAdd = async () => {
        const errors = validateOffice(officeItem);
        setOfficeErrors(errors);
        const hasErrors = Object.keys(errors).length > 0;

        if (hasErrors || officeItem.quantityofpatientsallowed < 0) return false;

        try {
            const savedOffice = await saveOffice(officeItem);
            const persistedOffice = (savedOffice as any)?.data ?? savedOffice;
            const completedOffice = {
                ...persistedOffice,
                active: officeItem.active,
                description: officeItem.description,
                sectorstring: selectedSector?.name ?? "",
                locationtypestring: selectedLocationType?.description ?? "",
                quantityofpatientsallowed: officeItem.quantityofpatientsallowed
            };
            const updatedOffices: Office[] = editIndex === null
                ? [...offices, completedOffice]
                : offices.map((office, index) => (index === editIndex ? completedOffice : office));
            onChange(updatedOffices);

            dispatch(setNotification({
                title: !isEditing ? "Creación exitosa" : "Edición exitosa",
                desc: !isEditing ? 'Se ha creado el consultorio' : 'Se ha editado el consultorio',
                icon: 'pi pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO
            }));

            resetEditor();
            return true;
        } catch (error) {
            dispatch(setNotification({
                title: "Error",
                desc: "No se pudo guardar el consultorio",
                icon: 'pi-times-circle',
                typeOfNotification: ETypeOfNotification.ERROR
            }));
            return false;
        }
    };

    const handleEdit = (item: Office, index: number) => {
        const data = (item as any).data ?? item; // extrae `data` si viene de Axios
        setOfficeItem(data);
        setOfficeErrors({});

        const sector = sectors.find(s => s.name === data.sectorstring); // o usá `s.sector === data.sector` si lo tenés
        setSelectedSector(sector ?? null);

        setSelectedLocationType({
            locationtype: data.locationtype,
            description: data.locationtypestring
        });
        setEditIndex(index);
    };

    const handleToggleActive = async (item: Office, index: number) => {
    try {
        const fn = item.active ? inactivateOffice : activateOffice;
        await fn(item.hospitallocation);

        const updated = [...offices];
        updated[index] = { ...item, active: !item.active };
        onChange(updated);
    } catch {
        dispatch(setNotification({
        title: "Error",
        desc: "No se pudo cambiar el estado del consultorio",
        icon: 'pi pi-times-circle',
        typeOfNotification: ETypeOfNotification.ERROR
            }));
        }   
    };

    const validateOffice = (office: Office): { name?: string; officeType?: string; sector?: string } => {
        const errors: { name?: string; officeType?: string; sector?: string } = {};
        if (!office.description.trim()) errors.name = "El nombre es requerido";
        if (office.locationtype <= 0 || !office.locationtypestring.trim()) errors.officeType = "Debe seleccionar un tipo de consultorio";
        if (!office.sector || office.sector <= 0) errors.sector = "Debe seleccionar un sector";
        return errors;
    };

    const cancelEdit = () => {
        resetEditor();
    };

    const columns: InputColumnTableProps<Office>[] = [
        {
            header: "Sector",
            className: "location-inputtable-fluid-col location-col-office-sector",
            body: (item) => item.sectorstring,
            input: (
                <Combobox
                    key={"combosectorid"}
                    className={(officeErrors.sector ? "":"border-grey")+" w-full"}
                    getItems={() => Promise.resolve(sectors)}
                    value={selectedSector?.sector > 0 ? selectedSector : null}
                    setValue={(e: Sector | null) => {
                        if (e) {
                            setSelectedSector(e);
                            setOfficeItem({ ...officeItem, sector: e.sector, sectorstring: e.name });
                            setOfficeErrors({ ...officeErrors, sector: "" });
                        } else {
                            setSelectedSector(null);
                            setOfficeItem({ ...officeItem, sector: 0, sectorstring: "" });
                        }
                    }}
                    optionLabel="name"
                    error={!!officeErrors.sector}
                    caption={officeErrors.sector}
                />
            ),
        },{
            header: "Nombre",
            className: "location-inputtable-fluid-col location-col-office-name",
            body: (item) => item.description,
            input: (
                <InputTextCustom
                    key={"inputofficenameid"}
                    className={(officeErrors.name ? "":"border-grey")+" w-full"}
                    value={officeItem.description ?? ""}
                    onChange={(e) => {
                        setOfficeItem({ ...officeItem, description: e.target.value })
                        setOfficeErrors({ ...officeErrors, name: "" })
                    }}
                    error={!!officeErrors.name}
                    caption={officeErrors.name}
                />
            ),
        },{
            header: "Tipo de consultorio",
            className: "location-inputtable-fluid-col location-col-office-type",
            body: (item) => item.locationtypestring,
            input: (
                <Combobox
                    key={"combolocationtypeid"}
                    className={(officeErrors.officeType ? "":"border-grey")+" w-full"}
                    getItems={getLocationTypes}
                    params={{onlyOffices: true}}
                    value={selectedLocationType}
                    setValue={(e: LoacationType) => {
                        setSelectedLocationType(e)
                        setOfficeItem({ ...officeItem, locationtype: e.locationtype, locationtypestring: e.description })
                        setOfficeErrors({ ...officeErrors, officeType: "" })
                    }}
                    optionLabel="description"
                    error={!!officeErrors.officeType}
                    caption={officeErrors.officeType}
                />
            ),
        },{
            header: "Capacidad",
            className: "location-inputtable-fluid-col location-col-office-capacity",
            body: (item) => item.quantityofpatientsallowed,
            input: (
                <InputTextCustom
                    key={"inputcapacityid"}
                    className={"border-grey w-full"}
                    value={officeItem.quantityofpatientsallowed ? String(officeItem.quantityofpatientsallowed):""}
                    onChange={(e) => setOfficeItem({ ...officeItem, quantityofpatientsallowed: e.target.value >= 0 ? e.target.value : 0})}
                />
            ),
        },{
            header: "Estado",
            className: "location-inputtable-fluid-col location-col-office-state",
            body: (item) => (
                <span>
                    {item.active ? "Activo" : "Inactivo"}
                </span>
            ),
            input: (
                <div className="flex align-items-center gap-2 mt-2 location-switch-inline-row location-desktop-inputtable-switch-row">
                    <InputSwitch
                        id="inputswitchactiveid"
                        className="w-full"
                        checked={officeItem.active}
                        onChange={(e) => setOfficeItem({ ...officeItem, active: e.value })}
                    />
                    <span className="text-sm location-switch-state-label">{officeItem.active ? "Activo" : "Inactivo"}</span>
                </div>
            ),
        },{
            header: "",
            className: "location-inputtable-control-column",
            input: (
                isEditing ? (
                    <LocationInlineEditActions
                        visible
                        onConfirm={handleAdd}
                        onCancel={cancelEdit}
                    />
                ) : (
                    <Button
                        type="button"
                        icon="pi pi-plus-circle text-3xl"
                        className="buttonSecondary2 p-button-rounded location-inputtable-add-inline-btn"
                        onClick={handleAdd}
                        aria-label="Crear"
                    />
                )
            ),
            body: (item, index) => {
                const menuItems: LocationActionItem[] = [
                    {
                        label: "Editar",
                        icon: "pi pi-file-edit",
                        action: () => handleEdit(item, index),
                    },{
                        label: item.active ? "Desactivar" : "Activar",
                        icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                        action: () => handleToggleActive(item, index),
                    },{
                        label: "Eliminar",
                        icon: "pi pi-trash",
                        action: () => onDelete(item, index),
                    },
                ];

                return (
                    <div className="flex justify-content-end">
                        <LocationActionMenu
                            items={menuItems}
                            ariaLabel="Opciones del consultorio"
                            buttonId="buttonMenuItemId"
                            buttonClassName="p-button-text location-inputtable-menu-btn"
                        />
                    </div>
                );
            },
        },
    ];

    function closeMobileModal() {
        setMobileModalVisible(false);
        resetEditor();
    }

    function openAddModal() {
        resetEditor();
        setMobileModalVisible(true);
    }

    function openEditModal(item: Office, index: number) {
        handleEdit(item, index);
        setMobileModalVisible(true);
    }

    async function saveFromMobileModal() {
        const success = await executeAdd();
        if (success) {
            setMobileModalVisible(false);
        }
    }

    function formatCardValue(value: string | null | undefined, emptyText = "Sin datos") {
        const normalized = String(value ?? "").trim();
        return normalized !== "" ? normalized : emptyText;
    }

    //border-divider hace que se vea el divider en la tabla
    if (isMobile) {
        return (
            <div className="location-config-view">
                <div className="location-mobile-block-header">
                    <div>
                        <h4 className="location-mobile-card-title">{formatCardValue(mobileTitle, "Establecimiento")}</h4>
                        {!!mobileSubtitle && <p className="grey-color text-sm mt-1 mb-0">{mobileSubtitle}</p>}
                    </div>
                    <Button
                        className="location-mobile-add-btn"
                        icon="pi pi-plus-circle"
                        label="Agregar"
                        onClick={openAddModal}
                    />
                </div>

                <div className="location-mobile-card-list">
                    {(offices ?? []).map((item, index) => {
                        const menuItems: LocationMobileActionItem[] = [
                            {
                                label: "Editar",
                                icon: "pi pi-file-edit",
                                action: () => openEditModal(item, index),
                            },
                            {
                                label: item.active ? "Desactivar" : "Activar",
                                icon: item.active ? "pi pi-minus-circle" : "pi pi-check-circle",
                                action: () => { void handleToggleActive(item, index); },
                            },
                            {
                                label: "Eliminar",
                                icon: "pi pi-trash",
                                action: () => onDelete(item, index),
                            },
                        ];

                        return (
                            <article key={`location-office-${item.hospitallocation}-${index}`} className="location-mobile-card">
                                <div className="location-mobile-card-header">
                                    <h4 className="location-mobile-card-title">{formatCardValue(item.description, "Sin nombre")}</h4>
                                    <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones del consultorio" />
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Sector</span>
                                    <span className="location-mobile-card-value">{formatCardValue(item.sectorstring)}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Tipo</span>
                                    <span className="location-mobile-card-value">{formatCardValue(item.locationtypestring)}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Capacidad</span>
                                    <span className="location-mobile-card-value">{item.quantityofpatientsallowed ?? 0}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Estado</span>
                                    <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                                </div>
                            </article>
                        );
                    })}

                    {(offices ?? []).length === 0 && (
                        <p className="location-mobile-empty">No hay consultorios cargados.</p>
                    )}
                </div>

                <Modal
                    className="location-mobile-editor-modal"
                    visible={mobileModalVisible}
                    setVisible={setMobileModalVisible}
                    width="w-medium"
                    onHideCustom={closeMobileModal}
                    header={<h1 className="info-dark text-2xl">{officeItem.hospitallocation > 0 ? "Editar consultorio" : "Agregar consultorio"}</h1>}
                    footerButtonLeftText="Cancelar"
                    onClickLeftBtn={closeMobileModal}
                    footerButtonRightText={officeItem.hospitallocation > 0 ? "Guardar" : "Agregar"}
                    onClickRightBtn={() => { void saveFromMobileModal(); }}
                >
                    <div className="location-mobile-modal-content">
                        <div>
                            <label className="block mb-2">Sector <span className="text-red-500">*</span></label>
                            <Combobox
                                className={(officeErrors.sector ? "" : "border-grey") + " w-full"}
                                getItems={() => Promise.resolve(sectors)}
                                value={selectedSector?.sector > 0 ? selectedSector : null}
                                setValue={(e: Sector | null) => {
                                    if (e) {
                                        setSelectedSector(e);
                                        setOfficeItem({ ...officeItem, sector: e.sector, sectorstring: e.name });
                                        setOfficeErrors({ ...officeErrors, sector: "" });
                                    } else {
                                        setSelectedSector(null);
                                        setOfficeItem({ ...officeItem, sector: 0, sectorstring: "" });
                                    }
                                }}
                                optionLabel="name"
                                error={!!officeErrors.sector}
                                caption={officeErrors.sector}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">Nombre <span className="text-red-500">*</span></label>
                            <InputTextCustom
                                className={(officeErrors.name ? "" : "border-grey") + " w-full"}
                                value={officeItem.description ?? ""}
                                onChange={(e) => {
                                    setOfficeItem({ ...officeItem, description: e.target.value });
                                    setOfficeErrors({ ...officeErrors, name: "" });
                                }}
                                error={!!officeErrors.name}
                                caption={officeErrors.name}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">Tipo de consultorio <span className="text-red-500">*</span></label>
                            <Combobox
                                className={(officeErrors.officeType ? "" : "border-grey") + " w-full"}
                                getItems={getLocationTypes}
                                params={{ onlyOffices: true }}
                                value={selectedLocationType}
                                setValue={(e: LoacationType) => {
                                    setSelectedLocationType(e);
                                    setOfficeItem({ ...officeItem, locationtype: e.locationtype, locationtypestring: e.description });
                                    setOfficeErrors({ ...officeErrors, officeType: "" });
                                }}
                                optionLabel="description"
                                error={!!officeErrors.officeType}
                                caption={officeErrors.officeType}
                            />
                        </div>

                        <div>
                            <label className="block mb-2">Capacidad</label>
                            <InputTextCustom
                                className="border-grey w-full"
                                value={officeItem.quantityofpatientsallowed ? String(officeItem.quantityofpatientsallowed) : ""}
                                onChange={(e) => setOfficeItem({ ...officeItem, quantityofpatientsallowed: e.target.value >= 0 ? e.target.value : 0 })}
                            />
                        </div>

                        <div className="flex align-items-center gap-2 location-switch-inline-row">
                            <InputSwitch
                                id="inputswitchactivemobileofficeid"
                                checked={officeItem.active}
                                onChange={(e) => setOfficeItem({ ...officeItem, active: e.value })}
                            />
                            <span className="location-switch-state-label">{officeItem.active ? "Activo" : "Inactivo"}</span>
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }

    return (
        <InputTable
            className="location-inputtable-fluid location-inputtable-fluid--office"
            columnGap={0}
            headerDivClassName="font-semibold bg-bluegray-50"
            dividerClassName="my-1 border-divider"
            buttonColumnWidth={43}
            items={offices ?? []}
            columns={columns}
            onAdd={() => {}}
            divBtnClassName="location-inputtable-hide-native-control"
            divider
            dividerInFirstItem
        />
    );
}

